import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CategoryItem from "../components/CategoryItem";
import { useProductStore } from "../stores/useProductStore";
import FeaturedProducts from "../components/FeaturedProducts";
import { motion } from "framer-motion";
import { Sparkles, Heart, Star } from "lucide-react";
import { getImageUrl, getProductImages } from "../lib/imageUtils";

// Template for categories - counts and images will be calculated dynamically
const categoryTemplates = [
	{ href: "/category/necklaces", name: "שרשראות", icon: <Sparkles className="w-6 h-6" />, category: "necklaces" },
	{ href: "/category/earrings", name: "עגילים", icon: <Heart className="w-6 h-6" />, category: "earrings" },
	{ href: "/category/bracelets", name: "צמידים", icon: <Star className="w-6 h-6" />, category: "bracelets" },
];

const HomePage = () => {
	const navigate = useNavigate();
	
	// Safe store access with error handling
	const fetchAllProducts = useProductStore((state) => state.fetchAllProducts);
	const storeProducts = useProductStore((state) => state.products);
	const loading = useProductStore((state) => state.loading) || false;
	
	const products = useMemo(() => storeProducts || [], [storeProducts]);
	const getHomeProductImage = useCallback((product, fallback = "/images/placeholder.svg") => {
		return getProductImages(product)[0] || getImageUrl(fallback);
	}, []);
	
	// State for featured products
	const [featuredEarring, setFeaturedEarring] = useState(null);
	const [saleBracelets, setSaleBracelets] = useState([]);
	const [latestProducts, setLatestProducts] = useState([]);
	const [rotatingProduct, setRotatingProduct] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0); // מפתח לרענון

	// Navigation helpers with validation
	const handleProductNavigation = useCallback((product) => {
		if (!product || !product._id) {
			console.error('❌ HomePage: No product ID available for navigation');
			return;
		}
		console.log('🚀 HomePage: Navigating to product', product._id);
		navigate(`/product/${product._id}`);
	}, [navigate]);

	const handleCategoryNavigation = useCallback((category) => {
		if (!category) {
			console.error('❌ HomePage: No category provided for navigation');
			return;
		}
		console.log('🚀 HomePage: Navigating to category', category);
		navigate(`/category/${category}`);
	}, [navigate]);

	const handlePageNavigation = useCallback((path) => {
		if (!path) {
			console.error('❌ HomePage: No path provided for navigation');
			return;
		}
		console.log('🚀 HomePage: Navigating to page', path);
		navigate(path);
	}, [navigate]);

	useEffect(() => {
		try {
			fetchAllProducts();
		} catch (error) {
			console.error('Fetch products error:', error);
		}
	}, [fetchAllProducts]);
	
	// רענון אוטומטי של הבחירה כל 30 שניות
	useEffect(() => {
		const refreshInterval = setInterval(() => {
			setRefreshKey(prev => prev + 1);
		}, 30000); // 30 שניות
		
		return () => clearInterval(refreshInterval);
	}, []);

	// פונקציה לבחירת מוצרים ייחודיים - גישה שונה
	const selectUniqueProducts = useCallback(() => {
		if (!products || products.length === 0) return;
		
		console.log('🎯 מתחיל בחירת מוצרים ייחודיים...');
		console.log('📦 סה"כ מוצרים זמינים:', products.length);
		
		// מערך גלובלי לתמונות בשימוש
		const globalUsedImages = new Set();
		
		// פונקציה לבחירת מוצרים איכותיים
		const getQualityProducts = (categoryFilter = null, count = 1) => {
			let filtered = products.filter(p => {
				const image = getHomeProductImage(p);
				return p.price && 
					p.name &&
					image &&
					!['N01.jpg', 'N1.jpg', 'B01.jpg', 'B1.jpg', 'E01.jpg', 'E1.jpg'].some(def => image.includes(def));
			});
			
			// סינון לפי קטגוריה אם נדרש
			if (categoryFilter) {
				filtered = filtered.filter(p => p.category === categoryFilter);
			}
			
			// הסרת תמונות שכבר בשימוש
			filtered = filtered.filter(p => !globalUsedImages.has(getHomeProductImage(p)));
			
			console.log(`🔍 מוצרים זמינים עבור ${categoryFilter || 'כללי'}: ${filtered.length}`);
			
			// בחירה רנדומלית
			const shuffled = [...filtered].sort(() => 0.5 - Math.random());
			const selected = shuffled.slice(0, count);
			
			// הוספה לרשימת השימוש
			selected.forEach(product => {
				const image = getHomeProductImage(product);
				if (image) globalUsedImages.add(image);
			});
			
			return selected;
		};
		
		// 1. בחירת עגיל מומלץ
		const selectedEarrings = getQualityProducts('earrings', 1);
		if (selectedEarrings.length > 0) {
			setFeaturedEarring(selectedEarrings[0]);
			console.log('✅ עגיל נבחר:', selectedEarrings[0].name?.substring(0, 30), getHomeProductImage(selectedEarrings[0]));
		}
		
		// 2. צמידים במבצע - 3 צמידים (מחירים אמיתיים)
		const selectedBracelets = getQualityProducts('bracelets', 3);
		if (selectedBracelets.length > 0) {
			const braceletsOnSale = selectedBracelets.map(bracelet => ({
				...bracelet,
				isOnSale: true // רק מציין שזה במבצע, אבל מחיר אמיתי
			}));
			setSaleBracelets(braceletsOnSale);
			console.log('✅ צמידים במבצע נבחרו:', selectedBracelets.length, 'צמידים עם מחירים אמיתיים');
		}
		
		// 3. מוצרים חדשים - 6 מוצרים מכל הקטגוריות
		const selectedLatest = getQualityProducts(null, 6);
		if (selectedLatest.length > 0) {
			const latestWithTag = selectedLatest.map(product => ({
				...product,
				isNewProduct: true
			}));
			setLatestProducts(latestWithTag);
			console.log('✅ מוצרים חדשים נבחרו:', selectedLatest.length, 'מוצרים');
		}
		
		// 4. מוצר לרוטציה
		const selectedRotating = getQualityProducts(null, 1);
		if (selectedRotating.length > 0) {
			setRotatingProduct(selectedRotating[0]);
			console.log('✅ מוצר מתחלף נבחר:', selectedRotating[0].name?.substring(0, 30), getHomeProductImage(selectedRotating[0]));
		}
		
		console.log(`🎯 סיום בחירה - ${globalUsedImages.size} תמונות ייחודיות נבחרו:`, Array.from(globalUsedImages));
	}, [products, getHomeProductImage]);

	// Effect to select featured products with unique selection logic
	useEffect(() => {
		selectUniqueProducts();
	}, [products, refreshKey, selectUniqueProducts]); // הוספת refreshKey כדי לכפות רענון

	// טיימר לחילוף מוצרים כל 8 שניות - עם מניעת כפילויות
	useEffect(() => {
		if (!products || products.length === 0 || !featuredEarring || !saleBracelets.length || !latestProducts.length) return;

		// איסוף כל התמונות שכבר מוצגות כרגע
		const getCurrentlyDisplayedImages = () => {
			const displayed = new Set();
			
			// תמונה מהעגיל המוצג
			if (featuredEarring) displayed.add(getHomeProductImage(featuredEarring));
			
			// תמונות מצמידים במבצע
			saleBracelets.forEach(bracelet => {
				displayed.add(getHomeProductImage(bracelet));
			});
			
			// תמונות מהמוצרים החדשים
			latestProducts.forEach(product => {
				displayed.add(getHomeProductImage(product));
			});
			
			return displayed;
		};

		const getAvailableProducts = () => {
			const currentlyDisplayed = getCurrentlyDisplayedImages();
			return products.filter(p => {
				const image = getHomeProductImage(p);
				return p.price && 
					p.name &&
					image &&
					!['N01.jpg', 'N1.jpg', 'B01.jpg', 'B1.jpg', 'E01.jpg', 'E1.jpg'].some(def => image.includes(def)) &&
					!currentlyDisplayed.has(image);
			});
		};

		const interval = setInterval(() => {
			const availableProducts = getAvailableProducts();
			if (availableProducts.length > 0) {
				const currentIndex = Math.floor(Math.random() * availableProducts.length);
				setRotatingProduct(availableProducts[currentIndex]);
			}
		}, 8000); // 8 שניות

		return () => clearInterval(interval);
	}, [products, featuredEarring, saleBracelets, latestProducts, getHomeProductImage]);

	// Dynamic category counts calculation
	const categories = useMemo(() => {
		if (!products || products.length === 0) {
			// Return categories with loading message while products are being fetched
			return categoryTemplates.map(template => ({
				...template,
				count: loading ? "טוען..." : "0 מוצרים"
			}));
		}

		// Calculate real counts and get dynamic images from products
		return categoryTemplates.map(template => {
			// Count and find products by category
			const categoryProducts = products.filter(product => {
				if (!product || !product.category) return false;
				
				// Convert category names to match product categories
				const categoryMapping = {
					'necklaces': ['necklaces', 'necklace', 'שרשראות', 'שרשרת'],
					'earrings': ['earrings', 'earring', 'עגילים', 'עגיל'], 
					'bracelets': ['bracelets', 'bracelet', 'צמידים', 'צמיד']
				};
				
				const categoryVariations = categoryMapping[template.category] || [template.category];
				return categoryVariations.some(variation => 
					product.category.toLowerCase().includes(variation.toLowerCase())
				);
			});

			// Get a random product image for this category
			const randomProduct = categoryProducts.length > 0 
				? categoryProducts[Math.floor(Math.random() * categoryProducts.length)]
				: null;

			return {
				...template,
				count: `${categoryProducts.length} מוצרים`,
				imageUrl: randomProduct ? getHomeProductImage(randomProduct, '/images/placeholder.jpg') : '/images/placeholder.jpg'
			};
		});
	}, [products, loading, getHomeProductImage]);

	console.log('📦 מוצרים בדף הבית:', products?.length || 0);
	console.log('🔄 Loading status:', loading);
	console.log('📊 Categories with counts:', categories);
	console.log('🏪 Store data:', { products: products?.slice(0, 2), loading });

	return (
		<div className='relative min-h-screen text-white overflow-hidden bg-gradient-to-br from-white via-white to-white'>
			{/* Background Pattern */}
			<div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(241, 231, 231, 0.1)_1px,transparent_0)] [background-size:20px_20px]" />
			
			{/* Hero Section - Modern Card Layout */}
			<div className='relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 lg:pt-32 lg:pb-16'>
				<div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mb-16">
					
					{/* Live Products Hero Gallery */}
					<motion.div 
						className="lg:col-span-8 rounded-3xl p-6 lg:p-8 bg-white shadow-2xl border border-gray-100"
						initial={{ opacity: 0, x: -50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
					>
						<div className="flex items-center justify-between mb-6">
							<div className="flex items-center gap-3">
								{/* <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{backgroundColor: 'rgba(91, 12, 61, 0.1)'}}>
									<Sparkles className="w-6 h-6" style={{color: '#5b0c3d'}} />
								</div> */}
								<div>
									{/* <h1 className='text-3xl lg:text-4xl font-bold' style={{color: '#5b0c3d'}}>
										SHA-SHA
									</h1>
									<p className='text-gray-600 text-lg'>
										By Nataly
									</p> */}
									<img 
										src="./sha-sha-logo.svg" 
										alt="SHA-SHA By Nataly" 
										className="h-10 min-w-12 sm:h-12 md:h-14 lg:h-16 xl:h-20 bg-white drop-shadow-sm hover:drop-shadow-md transition-all rounded-2xl duration-300"
									/>
								</div>
							</div>
							<div className="flex items-center gap-4">
								<div className="flex items-center gap-2 text-sm text-gray-600">
									<div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
									<span>מוצרים במלאי</span>
								</div>
								<button 
									onClick={() => setRefreshKey(prev => prev + 1)}
									className="text-xs px-3 py-1 rounded-full border transition-colors hover:bg-gray-50"
									style={{borderColor: '#5b0c3d', color: '#5b0c3d'}}
								>
									🔄 רענון
								</button>
							</div>
						</div>
						
						<motion.div 
							key={rotatingProduct?._id || 'loading'}
							className="mb-6 p-4 bg-gradient-to-r from-red-50 to-gray-100 rounded-2xl border border-gray-200"
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.5 }}
						>
							{rotatingProduct ? (
								<div className="flex items-center gap-4">
									<div className="relative cursor-pointer" onClick={() => handleProductNavigation(rotatingProduct)}>
										<img 
											src={getHomeProductImage(rotatingProduct, '/images/products/N11.jpg')} 
											alt={rotatingProduct.name}
											className="w-24 h-24 object-cover rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
											onError={(e) => {
												e.target.src = '/images/products/N11.jpg';
											}}
										/>
										<div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse">
											עכשיו
										</div>
									</div>
									<div className="flex-1">
										<h3 className="text-lg font-bold mb-1 cursor-pointer hover:underline" 
											style={{color: '#5b0c3d'}}
											onClick={() => handleProductNavigation(rotatingProduct)}>
											{rotatingProduct.name}
										</h3>
										<p className="text-gray-600 text-sm mb-2">
											קטגוריה: {rotatingProduct.category === 'necklaces' ? 'שרשראות' : 
													  rotatingProduct.category === 'bracelets' ? 'צמידים' : 
													  rotatingProduct.category === 'earrings' ? 'עגילים' : rotatingProduct.category}
											{rotatingProduct.stone && ` • אבן: ${rotatingProduct.stone}`}
											{rotatingProduct.sku && ` • מק"ט: ${rotatingProduct.sku}`}
										</p>
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												<div className="text-2xl font-bold" style={{color: '#5b0c3d'}}>
													₪{rotatingProduct.price}
												</div>
												<div className="text-green-600 text-sm font-semibold">
													מחיר מעודכן
												</div>
											</div>
											<div className="flex items-center gap-2 text-sm text-gray-500">
												<div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
												<span>חדש במלאי</span>
											</div>
										</div>
										<div className="flex items-center justify-between mt-2">
											{rotatingProduct.quantity !== undefined && (
												<p className="text-xs text-gray-500">
													במלאי: {rotatingProduct.quantity} יחידות
												</p>
											)}
											{rotatingProduct.inStock !== undefined && (
												<div className={`text-xs px-2 py-1 rounded-full ${rotatingProduct.inStock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
													{rotatingProduct.inStock ? 'זמין' : 'אזל מהמלאי'}
												</div>
											)}
										</div>
									</div>
								</div>
							) : (
								<div className="flex items-center gap-4">
									<div className="w-24 h-24 bg-gray-300 rounded-xl animate-pulse"></div>
									<div className="flex-1">
										<div className="h-6 bg-gray-300 rounded mb-2 animate-pulse"></div>
										<div className="h-4 bg-gray-200 rounded mb-2 w-3/4 animate-pulse"></div>
										<div className="h-8 bg-gray-300 rounded w-1/2 animate-pulse"></div>
									</div>
								</div>
							)}
						</motion.div>
						
						<div className="text-center mb-4">
							{/* <h2 className="text-xl lg:text-2xl font-bold mb-2" style={{color: '#5b0c3d'}}>
								קולקציה חדשה ויוקרתית ✨
							</h2> */}
							{/* <p className="text-gray-600 text-lg">
								{categories.find(c => c.name === 'שרשראות')?.count || 56} שרשראות • 
								{categories.find(c => c.name === 'צמידים')?.count || 38} צמידים • 
								{categories.find(c => c.name === 'עגילים')?.count || 46} עגילים
							</p> */}
							<p className="text-gray-500 text-xl mt-1">
								תכשיטים מיוחדים בעבודת יד ואבנים טבעיות
							</p>
						</div>
						
						{loading ? (
							<div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
								{[1,2,3,4,5,6].map(i => (
									<div key={i} className="relative">
										<div className="w-full aspect-square bg-gray-200 rounded-xl animate-pulse"></div>
										<div className="absolute top-2 right-2 w-12 h-6 bg-gray-300 rounded animate-pulse"></div>
									</div>
								))}
							</div>
						) : latestProducts.length > 0 ? (
							<div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
								{latestProducts.slice(0, 6).map((product, index) => (
									<motion.div 
										key={product._id || index}
										className="relative group cursor-pointer"
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.4, delay: index * 0.1 }}
										onClick={() => handleProductNavigation(product)}
									>
										<img 
											src={getHomeProductImage(product, '/images/products/N11.jpg')} 
											alt={product.name}
											className="w-full aspect-square object-cover rounded-xl group-hover:scale-105 transition-transform duration-300 shadow-md"
											onError={(e) => {
												e.target.src = '/images/products/N11.jpg';
											}}
										/>
										<div className="absolute top-2 right-2 bg-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg" style={{color: '#5b0c3d'}}>
											₪{product.price}
										</div>
										{product.isNewProduct && (
											<div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
												חדש
											</div>
										)}
										<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-xl flex items-center justify-center">
											<div className="text-white opacity-0 group-hover:opacity-100 text-sm font-semibold">
												👁️ צפו במוצר
											</div>
										</div>
										<div className="absolute bottom-2 left-2 right-2 bg-white bg-opacity-90 rounded-lg p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
											<p className="text-xs text-gray-800 font-semibold truncate">{product.name}</p>
										</div>
									</motion.div>
								))}
							</div>
						) : products.length > 0 ? (
							<div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
								{products.filter(p => getHomeProductImage(p) && p.price && p.name)
									.slice(0, 6)
									.map((product, index) => (
									<motion.div 
										key={product._id || index}
										className="relative group cursor-pointer"
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.4, delay: index * 0.1 }}
										onClick={() => handleProductNavigation(product)}
									>
										<img 
											src={getHomeProductImage(product, '/images/products/N11.jpg')} 
											alt={product.name}
											className="w-full aspect-square object-cover rounded-xl shadow-md group-hover:scale-105 transition-transform duration-300"
											onError={(e) => {
												e.target.src = '/images/products/N11.jpg';
											}}
										/>
										<div className="absolute top-2 right-2 bg-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg" style={{color: '#5b0c3d'}}>
											₪{product.price}
										</div>
										{index < 3 && (
											<div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
												חדש
											</div>
										)}
										{!product.inStock && (
											<div className="absolute bottom-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
												אזל
											</div>
										)}
										<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-xl flex items-center justify-center">
											<div className="text-white opacity-0 group-hover:opacity-100 text-sm font-semibold">
												👁️ צפו במוצר
											</div>
										</div>
										<div className="absolute bottom-2 left-2 right-2 bg-white bg-opacity-90 rounded-lg p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
											<p className="text-xs text-gray-800 font-semibold truncate">{product.name}</p>
											<p className="text-xs text-gray-600">
												{product.category === 'necklaces' ? 'שרשרת' : 
												 product.category === 'bracelets' ? 'צמיד' : 
												 product.category === 'earrings' ? 'עגילים' : product.category}
											</p>
										</div>
									</motion.div>
								))}
							</div>
						) : (
							<div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
								{['/images/products/N11.jpg', '/images/products/B05.jpg', '/images/products/E18.jpg', '/images/products/N17.jpg', '/images/products/N29.jpg', '/images/products/B11.jpg'].map((img, i) => (
									<div key={i} className="relative group cursor-pointer">
										<img 
											src={getImageUrl(img)} 
											alt="מוצר מומלץ"
											className="w-full aspect-square object-cover rounded-xl shadow-md group-hover:scale-105 transition-transform duration-300"
										/>
										<div className="absolute top-2 right-2 bg-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg" style={{color: '#5b0c3d'}}>
											₪{180 + i * 25}
										</div>
										{i < 3 && (
											<div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
												חדש
											</div>
										)}
									</div>
								))}
							</div>
						)}
						
						<div className="flex flex-col sm:flex-row gap-4">
							<motion.button 
								className="px-6 py-3 text-white rounded-xl font-semibold hover:scale-105 transition-transform shadow-lg"
								style={{backgroundColor: '#5b0c3d'}}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								onClick={() => handlePageNavigation('/products')}
							>
								🔍 צפו בקולקציה
							</motion.button>
							<motion.button 
								className="px-6 py-3 border-2 rounded-xl font-semibold transition-colors hover:scale-105"
								style={{borderColor: '#5b0c3d', color: '#5b0c3d'}}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								onClick={() => handlePageNavigation('/contact')}
							>
								💬 צור קשר
							</motion.button>
						</div>
					</motion.div>
					
					{/* Side Cards */}
					<div className="lg:col-span-4 space-y-6">
						{/* Live Products Gallery */}
						<motion.div 
							className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, delay: 0.2 }}
						>
							<div className="flex items-center gap-3 mb-4">
								<div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: 'rgba(91, 12, 61, 0.1)'}}>
									<Heart className="w-4 h-4" style={{color: '#5b0c3d'}} />
								</div>
								<h3 className="font-semibold text-gray-800">מוצרים במלאי</h3>
								<div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
							</div>
							
							{loading ? (
								<div className="grid grid-cols-2 gap-2">
									{[1,2,3,4].map(i => (
										<div key={i} className="relative">
											<div className="w-full aspect-square bg-gray-200 rounded-lg animate-pulse"></div>
											<div className="absolute top-1 right-1 w-8 h-4 bg-gray-300 rounded animate-pulse"></div>
										</div>
									))}
								</div>
							) : latestProducts.length > 0 ? (
								<div className="grid grid-cols-2 gap-2">
									{latestProducts.slice(0, 4).map((product, index) => (
										<div 
											key={product._id || index}
											className="relative group cursor-pointer"
											onClick={() => handleProductNavigation(product)}
										>
											<img 
												src={getHomeProductImage(product, '/images/products/N11.jpg')} 
												alt={product.name}
												className="w-full aspect-square object-cover rounded-lg group-hover:scale-105 transition-transform duration-200"
												onError={(e) => {
													e.target.src = '/images/products/N11.jpg';
												}}
											/>
											<div className="absolute top-1 right-1 bg-white text-xs px-1 py-0.5 rounded font-semibold shadow" style={{color: '#5b0c3d'}}>
												₪{product.price}
											</div>
											{product.isNewProduct && (
												<div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-1 py-0.5 rounded font-semibold">
													חדש
												</div>
											)}
											<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
												<div className="text-white opacity-0 group-hover:opacity-100 text-xs font-semibold">
													🔍
												</div>
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="text-center text-gray-500 text-sm">
									<div className="text-2xl mb-2">📦</div>
									<p>טוען מוצרים...</p>
								</div>
							)}
							
							<div className="mt-4 text-center">
								<button 
									className="text-xs px-3 py-1 rounded-full font-semibold transition-colors"
									style={{backgroundColor: 'rgba(91, 12, 61, 0.1)', color: '#5b0c3d'}}
									onClick={() => handlePageNavigation('/products')}
								>
									צפו בכל המוצרים
								</button>
							</div>
						</motion.div>
						
						{/* Rotating Product Image Card */}
						<motion.div 
							key={rotatingProduct?._id || 'rotating-loading'}
							className="bg-white rounded-2xl p-4 shadow-xl border border-gray-100 overflow-hidden"
							initial={{ opacity: 0, y: 20, scale: 0.95 }}
							animate={{ opacity: 1, y: 0, scale: 1 }}
							transition={{ duration: 0.6 }}
						>
							{loading ? (
								<div className="relative">
									<div className="w-full aspect-square bg-gray-200 rounded-xl animate-pulse"></div>
									<div className="absolute top-3 right-3 w-16 h-6 bg-gray-300 rounded-full animate-pulse"></div>
									<div className="mt-3 space-y-2">
										<div className="h-4 bg-gray-200 rounded animate-pulse"></div>
										<div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse"></div>
									</div>
								</div>
							) : rotatingProduct ? (
								<div className="relative group cursor-pointer" onClick={() => handleProductNavigation(rotatingProduct)}>
									<img 
										src={getHomeProductImage(rotatingProduct, '/images/products/N11.jpg')} 
										alt={rotatingProduct.name}
										className="w-full aspect-square object-cover rounded-xl group-hover:scale-105 transition-transform duration-300 shadow-md"
										onError={(e) => {
											e.target.src = '/images/products/N11.jpg';
										}}
									/>
									<div className="absolute top-3 right-3 bg-white text-sm px-3 py-1 rounded-full font-bold shadow-lg" style={{color: '#5b0c3d'}}>
										₪{rotatingProduct.price}
									</div>
									<div className="absolute top-3 left-3 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse">
										עכשיו
									</div>
									<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-xl flex items-center justify-center">
										<div className="text-white opacity-0 group-hover:opacity-100 font-semibold">
											👁️ צפו במוצר
										</div>
									</div>
								</div>
							) : (
								<div className="relative">
									{latestProducts.length > 0 ? (
										<div className="relative group cursor-pointer" onClick={() => handleProductNavigation(latestProducts[0])}>
											<img 
												src={getHomeProductImage(latestProducts[0], '/images/products/N11.jpg')} 
												alt={latestProducts[0].name || "מוצר מומלץ"}
												className="w-full aspect-square object-cover rounded-xl shadow-md group-hover:scale-105 transition-transform duration-300"
											/>
											<div className="absolute top-3 right-3 bg-white text-sm px-3 py-1 rounded-full font-bold shadow-lg" style={{color: '#5b0c3d'}}>
												₪{latestProducts[0].price}
											</div>
										</div>
									) : (
										<div className="w-full aspect-square bg-gray-200 rounded-xl flex items-center justify-center">
											<span className="text-gray-500">טוען מוצרים...</span>
										</div>
									)}
								</div>
							)}
							
							{rotatingProduct && (
								<div className="mt-4 text-center">
									<h4 className="font-bold text-gray-800 text-sm mb-1 line-clamp-1">
										{rotatingProduct.name}
									</h4>
									<p className="text-xs text-gray-500">
										{rotatingProduct.category === 'necklaces' ? 'שרשרת' : 
										 rotatingProduct.category === 'bracelets' ? 'צמיד' : 
										 rotatingProduct.category === 'earrings' ? 'עגילים' : rotatingProduct.category}
									</p>
									<div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-400">
										<div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
										<span>חדש במלאי</span>
									</div>
								</div>
							)}
						</motion.div>
					</div>
				</div>
				
				{/* Building Trust Section */}
				<motion.div 
					className="bg-gradient-to-r from-gray-50 to-white rounded-3xl p-8 lg:p-12 mb-12 shadow-lg border border-gray-100"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.6 }}
				>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
						{/* Left Content */}
						<div>
							<div className="flex items-center gap-2 mb-4">
								<div className="w-10 h-10 rounded-full flex items-center justify-center" style={{backgroundColor: 'rgba(91, 12, 61, 0.1)'}}>
									<Star className="w-5 h-5" style={{color: '#5b0c3d'}} />
								</div>
								<h3 className="text-2xl font-bold text-gray-800">יוצרים תכשיטים יוקרתיים</h3>
							</div>
							<p className="text-gray-600 mb-6 leading-relaxed">
								כל תכשיט נוצר בקפידה עם חומרים איכותיים ועיצוב חדשני. האיכות שלנו מבוססת על ניסיון של שנים ותשומת לב לפרטים הקטנים.
							</p>
							
							{/* Features */}
							<div className="grid grid-cols-2 gap-4">
								<div className="flex items-center gap-2">
									<div className="w-6 h-6 rounded-full flex items-center justify-center" style={{backgroundColor: '#5b0c3d'}}>
										<Sparkles className="w-3 h-3 text-white" />
									</div>
									<span className="text-sm font-medium text-gray-700">בעבודת יד</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-6 h-6 rounded-full flex items-center justify-center" style={{backgroundColor: '#5b0c3d'}}>
										<Heart className="w-3 h-3 text-white" />
									</div>
									<span className="text-sm font-medium text-gray-700">איכות פרימיום</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-6 h-6 rounded-full flex items-center justify-center" style={{backgroundColor: '#5b0c3d'}}>
										<Star className="w-3 h-3 text-white" />
									</div>
									<span className="text-sm font-medium text-gray-700">עיצוב חדשני</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-6 h-6 rounded-full flex items-center justify-center" style={{backgroundColor: '#5b0c3d'}}>
										<Sparkles className="w-3 h-3 text-white" />
									</div>
									<span className="text-sm font-medium text-gray-700">חומרים איכותיים</span>
								</div>
							</div>
						</div>
						
						{/* Right Image/Stats */}
						<div className="relative">
							<div className="bg-white rounded-2xl p-6 shadow-lg">
								<div className="text-center mb-4">
									{loading ? (
										<div className="relative">
											<div className="w-full h-48 bg-gray-200 rounded-xl mb-4 animate-pulse"></div>
											<div className="absolute top-2 right-2 w-12 h-6 bg-gray-200 rounded-full animate-pulse"></div>
										</div>
									) : featuredEarring ? (
										<div className="relative group cursor-pointer" onClick={() => handleProductNavigation(featuredEarring)}>
											<img 
												src={getHomeProductImage(featuredEarring, '/images/products/N17.jpg')} 
												alt={featuredEarring.name || "מוצר יוקרתי"}
												className="w-full h-48 object-cover rounded-xl mb-4 group-hover:scale-105 transition-transform duration-300"
												onError={(e) => {
													e.target.src = '/images/products/N17.jpg';
												}}
											/>
											<div className="absolute top-2 right-2 bg-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg" style={{color: '#5b0c3d'}}>
												₪{featuredEarring.price}
											</div>
										</div>
									) : (
										<div className="w-full h-48 bg-gray-200 rounded-xl mb-4 flex items-center justify-center">
											<span className="text-gray-500">טוען מוצרים...</span>
										</div>
									)}
									<h4 className="font-bold text-lg text-gray-800 mb-2">יצירות מיוחדות</h4>
									<p className="text-sm" style={{color: '#5b0c3d'}}>SHA-SHA • ישראל</p>
								</div>
							</div>
						</div>
					</div>
				</motion.div>

				{/* Categories Grid */}
				<motion.div
					className='grid grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16'
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.2 }}
				>
					{categories.map((category, index) => (
						<motion.div
							key={category.name}
							itemID={index}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 * index }}
							 className={`border-5 drop-shadow-lg border-brand-primary rounded-lg ${index === category.count - 1 && 'col-span-3'}`}
							style={{
								boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.5)',
								transition: 'all 0.3s ease',
								transform: 'scale(1.05)',
								cursor: 'pointer',
								overflow: 'hidden',
								backgroundColor: 'rgba(0, 0, 0, 0.5)',
								backgroundBlendMode: 'overlay',
								backgroundSize: '100% 100%',
								backgroundPosition: 'center',
								backgroundRepeat: 'no-repeat',
								backgroundImage: `url(${category.imageUrl})`,
								
							}}
						>
							<CategoryItem category={category} className="bg-transparent" />
						</motion.div>
					))}
				</motion.div>

				{/* Sale Bracelets Section */}
				{saleBracelets.length > 0 && (
					<motion.div 
						className="mb-12 md:mb-16"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.3 }}
					>
						<div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-3xl p-8 border border-red-100">
							<div className="text-center mb-8">
								<h3 className="text-2xl lg:text-3xl font-bold mb-2" style={{color: '#5b0c3d'}}>
									צמידים במבצע מיוחד 🔥
								</h3>
								<p className="text-gray-600">
									{categories.find(c => c.name === 'צמידים')?.count || 46} מוצרים • 
									עיצוב ייחודי ואיכות פרימיום
								</p>
							</div>
							
							<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
								{saleBracelets.map((bracelet, index) => (
									<motion.div
										key={bracelet._id}
										className="bg-white rounded-2xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.6, delay: 0.1 * index }}
										onClick={() => handleProductNavigation(bracelet)}
									>
										<div className="relative mb-4">
											<img 
												src={getHomeProductImage(bracelet, '/images/products/B05.jpg')} 
												alt={bracelet.name}
												className="w-full h-40 object-cover rounded-xl group-hover:scale-105 transition-transform duration-300"
												onError={(e) => {
													e.target.src = '/images/products/B05.jpg';
												}}
											/>
											<div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-sm font-bold">
												במבצע
											</div>
											<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-xl flex items-center justify-center">
												<div className="text-white opacity-0 group-hover:opacity-100 font-semibold">
													👁️ צפו במוצר
												</div>
											</div>
										</div>
										
										<h4 className="font-semibold text-gray-800 mb-2 line-clamp-2">
											{bracelet.name}
										</h4>
										
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												<span className="font-bold text-lg" style={{color: '#5b0c3d'}}>
													₪{bracelet.price}
												</span>
												<span className="text-red-500 font-semibold text-sm">
													במבצע
												</span>
											</div>
											<div className="text-green-600 font-semibold text-xs">
												מחיר אמיתי
											</div>
										</div>
									</motion.div>
								))}
							</div>
							
							<div className="text-center mt-8">
								<button 
									className="px-6 py-3 rounded-full text-white font-semibold hover:scale-105 transition-transform duration-200"
									style={{backgroundColor: '#5b0c3d'}}
									onClick={() => handleCategoryNavigation('bracelets')}
								>
									צפו בכל הצמידים במבצע
								</button>
							</div>
						</div>
					</motion.div>
				)}

				{/* Featured Products Section */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.4 }}
				>
					<FeaturedProducts />
				</motion.div>

			</div>
		</div>
	);
};

export default HomePage;
